import {
  deriveIncomeTests,
  incomeTestsRulesFor,
  type IncomeComponents,
  type IncomeTestsOutput,
} from "../income-tests/index.js";
import {
  incomeTaxFor,
  medicareLevyFor,
  helpRepaymentFor,
  incomeTaxRulesFor,
} from "../income-tax/index.js";
import {
  type IncomeFigures,
  type MeansTestTrace,
  type PackagingShockInput,
  PackagingShockInputSchema,
  type PackagingShockOutput,
  type PackagingShockRuleSet,
} from "./types.js";

/**
 * Build the income-tests component set for one scenario. The packaged benefit
 * is modelled as:
 *   - a reduction in taxable income by the sacrificed cash (the cash forgone),
 *   - PLUS a reported RFBA (grossed-up) split by FBT class so income-tests can
 *     apply the 0.53 un-gross-up to ONLY the exempt-employer portion.
 *
 * When `withBenefit` is false, neither adjustment applies — that's the baseline
 * the flow-on is measured against. We never re-implement the add-back rules
 * here; we hand the components to income-tests.
 */
function componentsFor(
  input: PackagingShockInput,
  rules: PackagingShockRuleSet,
  withBenefit: boolean,
): IncomeComponents {
  const sacrificed = input.sacrificedCash ?? input.benefitTaxableValue;

  // RFBA is only reported when the benefit's TAXABLE value exceeds the trigger.
  const reportable =
    withBenefit && input.benefitTaxableValue > rules.rfbaReportingThreshold;
  const grossedUp = reportable
    ? input.benefitTaxableValue * rules.grossUpRate
    : 0;

  // Split the grossed-up RFBA by FBT class. income-tests un-grosses ONLY the
  // exempt-employer portion (factor 0.53) for CCS/FTB ATI; a novated lease sits
  // entirely in the non-exempt portion and counts in full everywhere.
  const exemptEmployer =
    input.packagingKind === "exempt_employer" ? grossedUp : 0;
  const nonExemptEmployer =
    input.packagingKind === "novated_lease" ? grossedUp : 0;

  const taxableIncome = withBenefit
    ? Math.max(0, input.grossSalary - sacrificed)
    : input.grossSalary;

  return {
    taxableIncome,
    reportableFringeBenefits: { exemptEmployer, nonExemptEmployer },
    reportableSuperContributions: input.reportableSuper,
    netFinancialInvestmentLoss: 0,
    netRentalPropertyLoss: 0,
    familyTrustDistributionTax: 0,
    exemptForeignEmploymentIncome: 0,
    taxFreePensionsBenefits: 0,
    targetForeignIncome: 0,
    childMaintenanceExpenditure: 0,
    superLumpSumTaxedElementsAboveCap: 0,
  };
}

function figuresFrom(out: IncomeTestsOutput): IncomeFigures {
  return {
    incomeForSurchargePurposes: out.incomeForSurchargePurposes,
    helpRepaymentIncome: out.helpRepaymentIncome,
    div293Income: out.div293Income,
    adjustedTaxableIncome: out.adjustedTaxableIncome,
    grossedUpFringeBenefits: out.grossedUpFringeBenefits,
    adjustedFringeBenefitsTotal: out.adjustedFringeBenefitsTotal,
  };
}

/** The family MLS threshold for the household (base + per-child increment). */
function familyMlsThreshold(
  input: PackagingShockInput,
  rules: PackagingShockRuleSet,
): number {
  const extraChildren = Math.max(0, input.children - 1);
  return rules.mls.familyBase + extraChildren * rules.mls.familyChildIncrement;
}

/** MLS tested income — own income for singles, combined for families. */
function mlsTestedIncome(
  input: PackagingShockInput,
  ownSurchargeIncome: number,
): number {
  return input.filingStatus === "family"
    ? ownSurchargeIncome + input.partnerIncome
    : ownSurchargeIncome;
}

/** A coarse MLS tier label for copy (Base when at/under the base threshold). */
function mlsTier(testedIncome: number, baseThreshold: number): string {
  return testedIncome > baseThreshold ? "above-base" : "Base";
}

/**
 * Trace the MLS flow-on. The benefit can push combined tested income over the
 * base threshold; the surcharge is levied on the person's OWN surcharge income.
 * If they hold hospital cover they're exempt — we still surface the income
 * crossing (information) but the levied flow-on is 0.
 */
function traceMls(
  input: PackagingShockInput,
  rules: PackagingShockRuleSet,
  without: IncomeFigures,
  withF: IncomeFigures,
): MeansTestTrace {
  const base =
    input.filingStatus === "family"
      ? familyMlsThreshold(input, rules)
      : rules.mls.singleBase;

  const testedWithout = mlsTestedIncome(input, without.incomeForSurchargePurposes);
  const testedWith = mlsTestedIncome(input, withF.incomeForSurchargePurposes);

  const tierWithout = mlsTier(testedWithout, base);
  const tierWith = mlsTier(testedWith, base);
  const crosses = tierWithout === "Base" && tierWith !== "Base";

  // The surcharge magnitude itself is owned by AU-08 (tiered 1–1.5% on own
  // income); here we only flag whether the benefit puts the household over the
  // base line and the levied flow-on (0 if covered, otherwise we leave the
  // tiered $ to AU-08 and report null to avoid double-sourcing the rate table).
  const flowOnAmount = input.holdsHospitalCover ? 0 : null;

  return {
    test: "mls",
    incomeWithout: testedWithout,
    incomeWith: testedWith,
    incomeDelta: testedWith - testedWithout,
    crossesThreshold: crosses,
    tierWithout,
    tierWith,
    flowOnAmount,
  };
}

/** Trace the Division 293 flow-on (crosses the $250k line → extra 15% on super). */
function traceDiv293(
  rules: PackagingShockRuleSet,
  without: IncomeFigures,
  withF: IncomeFigures,
): MeansTestTrace {
  const t = rules.div293Threshold;
  const below = "below";
  const above = "at-or-above";
  const tierWithout = without.div293Income >= t ? above : below;
  const tierWith = withF.div293Income >= t ? above : below;
  return {
    test: "div293",
    incomeWithout: without.div293Income,
    incomeWith: withF.div293Income,
    incomeDelta: withF.div293Income - without.div293Income,
    crossesThreshold: tierWithout === below && tierWith === above,
    tierWithout,
    tierWith,
    // Div293 is a 15% tax on the lesser of (income over $250k) and concessional
    // contributions — sizing it needs the contributions figure and apportioning
    // we don't model here; report null rather than a wrong number.
    flowOnAmount: null,
  };
}

/**
 * Trace the HELP/STSL flow-on. The grossed-up RFBA raises repayment income, so
 * a HELP debtor repays more. We CAN size this from the income-tax engine's HELP
 * bands, so we return the extra compulsory repayment the benefit causes.
 */
function traceHelp(
  input: PackagingShockInput,
  taxRules: ReturnType<typeof incomeTaxRulesFor>,
  without: IncomeFigures,
  withF: IncomeFigures,
): MeansTestTrace {
  const repayWithout = input.hasHelpDebt
    ? helpRepaymentFor(taxRules, without.helpRepaymentIncome)
    : 0;
  const repayWith = input.hasHelpDebt
    ? helpRepaymentFor(taxRules, withF.helpRepaymentIncome)
    : 0;
  return {
    test: "help",
    incomeWithout: without.helpRepaymentIncome,
    incomeWith: withF.helpRepaymentIncome,
    incomeDelta: withF.helpRepaymentIncome - without.helpRepaymentIncome,
    // A "threshold crossing" here is starting to repay (or moving up a band).
    crossesThreshold: input.hasHelpDebt && repayWith > repayWithout,
    tierWithout: null,
    tierWith: null,
    flowOnAmount: input.hasHelpDebt ? repayWith - repayWithout : 0,
  };
}

/**
 * Trace the CCS/FTB flow-on. The benefit raises ATI (combined with the
 * partner's). This is where the exempt-vs-non-exempt asymmetry shows: the
 * exempt-employer RFBA is un-grossed at 0.53, so the SAME taxable value moves
 * ATI less for exempt packaging than for a novated lease. We surface the ATI
 * delta (combined) — the CCS rate% itself is owned by AU-13, so flowOnAmount is
 * null (we never fabricate a subsidy figure).
 */
function traceCcs(
  input: PackagingShockInput,
  without: IncomeFigures,
  withF: IncomeFigures,
): MeansTestTrace {
  const combinedWithout = without.adjustedTaxableIncome + input.partnerAti;
  const combinedWith = withF.adjustedTaxableIncome + input.partnerAti;
  return {
    test: "ccs",
    incomeWithout: combinedWithout,
    incomeWith: combinedWith,
    incomeDelta: combinedWith - combinedWithout,
    // CCS tapers continuously (no single threshold here); a raised ATI always
    // means a lower or equal subsidy. We flag a "crossing" when ATI actually
    // rose, since that is the consequential event for the family.
    crossesThreshold: combinedWith > combinedWithout,
    tierWithout: null,
    tierWith: null,
    flowOnAmount: null,
  };
}

/**
 * The income-tax (+ Medicare levy) saving from sacrificing `sacrificedCash`.
 * Computed as the drop in income tax + Medicare levy between salary and
 * salary − sacrificed. This is the UPSIDE the surface must always show, so the
 * tool can never present the income-test downside in isolation.
 */
function incomeTaxSavingFor(
  input: PackagingShockInput,
  taxRules: ReturnType<typeof incomeTaxRulesFor>,
): number {
  const sacrificed = input.sacrificedCash ?? input.benefitTaxableValue;
  const reduced = Math.max(0, input.grossSalary - sacrificed);

  const household = {
    family: input.filingStatus === "family",
    dependants: input.children,
    seniorPensioner: false,
  };

  const taxOf = (taxable: number): number =>
    incomeTaxFor(taxRules, {
      taxableIncome: taxable,
      residency: "resident",
      residentMonths: 12,
    }) +
    medicareLevyFor(taxRules.medicareLevy, taxable, household, "resident");

  return Math.max(0, taxOf(input.grossSalary) - taxOf(reduced));
}

/**
 * Hard panel ship-condition: NO financial-product / novated-lease affiliate
 * identifier may reach a personalised result surface (misleading-by-omission +
 * AFSL s764A drift). This guard is called on the way out; if any forbidden
 * handoff token is attached to a payload it throws rather than ship a result
 * with a lease/financial-product link on it. Encoded as code, not a comment.
 */
const FORBIDDEN_HANDOFF_KEYS = [
  "affiliate",
  "affiliateUrl",
  "affiliateId",
  "leaseProvider",
  "leaseAffiliate",
  "novatedLeaseUrl",
  "referralUrl",
  "productHandoff",
  "financialProduct",
  "ctaUrl",
];

export function assertNoProductHandoff(payload: unknown): void {
  if (payload === null || typeof payload !== "object") return;
  const seen = new WeakSet<object>();
  const walk = (node: unknown): void => {
    if (node === null || typeof node !== "object") return;
    if (seen.has(node as object)) return;
    seen.add(node as object);
    for (const [key, value] of Object.entries(node)) {
      if (FORBIDDEN_HANDOFF_KEYS.includes(key)) {
        throw new Error(
          `packaging-shock: forbidden financial-product/lease handoff "${key}" ` +
            `cannot reach a personalised result surface (misleading-by-omission / AFSL s764A)`,
        );
      }
      walk(value);
    }
  };
  walk(payload);
}

/**
 * Calculate the packaging income-test shock. Pure: same inputs + rulesets always
 * yield the same output. Validates at the boundary. Returns BOTH the income-tax
 * saving (upside) and the per-test flow-on traces (downside) — never one without
 * the other (panel ship-condition, enforced by the type and this construction).
 */
export function calculatePackagingShock(
  rawInput: PackagingShockInput,
  rules: PackagingShockRuleSet,
): PackagingShockOutput {
  const input = PackagingShockInputSchema.parse(rawInput);
  const incomeTestsRules = incomeTestsRulesFor(rules.incomeTestsFy);
  const taxRules = incomeTaxRulesFor(rules.incomeTaxFy);

  const withoutOut = deriveIncomeTests(
    { components: componentsFor(input, rules, false), assessmentYear: rules.incomeTestsFy },
    incomeTestsRules,
  );
  const withOut = deriveIncomeTests(
    { components: componentsFor(input, rules, true), assessmentYear: rules.incomeTestsFy },
    incomeTestsRules,
  );

  const without = figuresFrom(withoutOut);
  const withF = figuresFrom(withOut);

  const means: MeansTestTrace[] = [
    traceMls(input, rules, without, withF),
    traceDiv293(rules, without, withF),
    traceHelp(input, taxRules, without, withF),
    traceCcs(input, without, withF),
  ];

  const output: PackagingShockOutput = {
    without,
    with: withF,
    incomeTaxSaving: incomeTaxSavingFor(input, taxRules),
    means,
    crossesAnyThreshold: means.some((m) => m.crossesThreshold),
  };

  // Guard: nothing financial-product/lease-shaped may ride out on the surface.
  assertNoProductHandoff(output);
  return output;
}
